<template>
  <div class="m7-dots" :class="[`m7-dots--${size}`, { 'is-editable': editable }]" role="group" :aria-label="ariaLabel">
    <component
      :is="editable ? 'button' : 'span'"
      v-for="(level, index) in accents"
      :key="index"
      class="m7-dots__dot"
      :class="[`is-level-${level}`, { 'is-flashing': flashing === index }]"
      :type="editable ? 'button' : undefined"
      :aria-label="
        editable ? `${$t('metronome.beat')} ${index + 1}: ${$t(`metronome.accents.${accentKey(level)}`)}` : undefined
      "
      @click="editable && emit('cycle', index)"
    />
  </div>
</template>

<script setup>
import { accentKey } from '~/utils/metronomeMeter'

const props = defineProps({
  accents: { type: Array, required: true },
  currentBeat: { type: Number, default: 0 },
  beatTick: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
  editable: { type: Boolean, default: false },
  size: { type: String, default: 'md' },
  ariaLabel: { type: String, default: undefined },
})

const emit = defineEmits(['cycle'])

// Drive the flash off beatTick rather than off currentBeat: in a one-beat bar
// the beat index never changes, and the dot would sit lit instead of pulsing.
const flashing = ref(-1)
let clearTimer = null

watch(
  () => props.beatTick,
  () => {
    if (!props.playing) return
    flashing.value = props.currentBeat
    if (clearTimer) clearTimeout(clearTimer)
    clearTimer = setTimeout(() => {
      flashing.value = -1
    }, 110)
  }
)

watch(
  () => props.playing,
  (isPlaying) => {
    if (!isPlaying) {
      flashing.value = -1
      if (clearTimer) clearTimeout(clearTimer)
    }
  }
)

onBeforeUnmount(() => {
  if (clearTimer) clearTimeout(clearTimer)
})
</script>

<style scoped>
.m7-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.m7-dots--sm {
  gap: 6px;
}

.m7-dots__dot {
  width: 12px;
  height: 12px;
  padding: 0;
  border-radius: 50%;
  border: 1.5px solid var(--m7-tick);
  background: var(--m7-tick);
  transition:
    transform 0.09s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.09s ease,
    border-color 0.09s ease,
    box-shadow 0.09s ease;
}

.m7-dots--sm .m7-dots__dot {
  width: 7px;
  height: 7px;
  border-width: 1px;
}

.is-editable .m7-dots__dot {
  cursor: pointer;
}

.is-editable .m7-dots__dot:focus-visible {
  outline: 2px solid var(--m7-accent);
  outline-offset: 3px;
}

/* Accent: larger and outlined so the downbeat is readable while stopped. */
.m7-dots__dot.is-level-2 {
  width: 16px;
  height: 16px;
  background: transparent;
  border-color: var(--m7-accent);
}

.m7-dots--sm .m7-dots__dot.is-level-2 {
  width: 10px;
  height: 10px;
}

/* Silent: a faint ring, so you can see the slot exists but hear nothing. */
.m7-dots__dot.is-level-0 {
  background: transparent;
  border-style: dashed;
}

.m7-dots__dot.is-flashing {
  background: var(--m7-accent);
  border-color: var(--m7-accent);
  transform: scale(1.35);
  box-shadow: 0 0 10px var(--m7-accent-glow);
}
</style>
